import React, { useRef } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { SectionFx, FxVariant } from './SectionFx';
interface SectionStageProps {
  children: ReactNode;
  intensity?: number;
  disabled?: boolean;
  fx?: FxVariant;
}
/**
 * Cinematic section wrapper. Adds:
 *   • Scroll-driven scale, opacity, and blur — the section zooms into focus
 *     as you scroll to it and dims/blurs when you scroll past
 *   • Optional animated background fx (particles, orbits, beams, etc.)
 */
export function SectionStage({
  children,
  intensity = 1,
  disabled = false,
  fx
}: SectionStageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const minScale = 1 - 0.18 * intensity;
  const minOpacity = 1 - 0.7 * intensity;
  const maxBlur = 10 * intensity;
  const scale = useTransform(scrollYProgress, [0, 0.32, 0.68, 1], [minScale, 1, 1, minScale]);
  const opacity = useTransform(scrollYProgress, [0, 0.28, 0.72, 1], [minOpacity, 1, 1, minOpacity]);
  const filter = useTransform(scrollYProgress, [0, 0.32, 0.68, 1], [`blur(${maxBlur}px)`, 'blur(0px)', 'blur(0px)', `blur(${maxBlur}px)`]) as MotionValue<string>;
  // Background-only fx parallax — counter-rotates slightly so the bg feels
  // like it's behind the content with depth
  const fxY = useTransform(scrollYProgress, [0, 1], ['-5%', '5%']);
  const fxScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);
  if (disabled) {
    return <div ref={ref} data-id="element-799">{children}</div>;
  }
  return <div ref={ref} className="relative" data-id="element-800">
      {/* Animated background fx */}
      {fx && fx !== 'none' && <motion.div className="absolute inset-0 pointer-events-none overflow-hidden" style={{
      y: fxY,
      scale: fxScale
    }} data-id="element-801">
          <SectionFx variant={fx} data-id="element-802" />
        </motion.div>}

      {/* Foreground content with dramatic zoom */}
      <motion.div className="relative" style={{
      scale,
      opacity,
      filter,
      willChange: 'transform, opacity, filter',
      transformOrigin: 'center center'
    }} data-id="element-803">
        {children}
      </motion.div>
    </div>;
}